import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, ArrowRight, PiggyBank, BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useInvestmentProducts, useInvestmentSimulation } from '../../hooks/useInvestments';

const typeLabels: Record<string, string> = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  FUND: 'Fundo',
  DEBENTURE: 'Debênture',
  CRI: 'CRI',
  CRA: 'CRA',
};

export function InvestmentSimulator() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('10000');
  const [term, setTerm] = useState('365');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [compareProducts, setCompareProducts] = useState<string[]>([]);

  const { data: productsData, isLoading: isLoadingProducts } = useInvestmentProducts({ isAvailable: true });
  const simulationMutation = useInvestmentSimulation();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatRate = (product: any) => {
    if (product.rateType === 'FIXED') {
      return `${(product.rate * 100).toFixed(2)}% a.a.`;
    }
    if (product.rateType === 'CDI_PERCENTAGE') {
      return `${(product.rate * 100).toFixed(0)}% do CDI`;
    }
    if (product.rateType === 'CDI_PLUS') {
      return `CDI + ${(product.rate * 100).toFixed(2)}%`;
    }
    if (product.rateType === 'IPCA_PLUS') {
      return `IPCA + ${(product.rate * 100).toFixed(2)}%`;
    }
    return `${(product.rate * 100).toFixed(2)}%`;
  };

  const handleSimulate = () => {
    if (!selectedProductId || !amount) return;
    simulationMutation.mutate({
      productId: selectedProductId,
      amount: Number(amount) * 100,
      term: Number(term),
    });
  };

  const handleCompare = () => {
    // In a real implementation, this would run multiple simulations
    setCompareMode(true);
  };

  const toggleCompareProduct = (productId: string) => {
    setCompareProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : prev.length < 3
        ? [...prev, productId]
        : prev
    );
  };

  const selectedProduct = productsData?.products?.find((p) => p.id === selectedProductId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Simulador de Investimentos</h1>
        <p className="text-surface-400">Compare e simule diferentes opções de investimento</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Simulation Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Parâmetros da Simulação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-surface-400 mb-2">Valor da Aplicação (R$)</label>
                  <Input
                    type="number"
                    placeholder="Ex: 10000"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-surface-400 mb-2">Prazo</label>
                  <Select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    options={[
                      { value: '30', label: '30 dias' },
                      { value: '90', label: '90 dias' },
                      { value: '180', label: '6 meses' },
                      { value: '365', label: '1 ano' },
                      { value: '730', label: '2 anos' },
                      { value: '1095', label: '3 anos' },
                      { value: '1825', label: '5 anos' },
                    ]}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-2">Produto</label>
                {isLoadingProducts ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    options={[
                      { value: '', label: 'Selecione um produto' },
                      ...(productsData?.products?.map((p) => ({
                        value: p.id,
                        label: `${p.name} - ${formatRate(p)}`,
                      })) || []),
                    ]}
                  />
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleSimulate}
                  disabled={!selectedProductId || !amount || simulationMutation.isPending}
                >
                  {simulationMutation.isPending ? 'Calculando...' : 'Simular'}
                </Button>
                <Button variant="secondary" onClick={() => setCompareMode(!compareMode)}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {compareMode ? 'Modo Simples' : 'Comparar Produtos'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Simulation Result */}
          {simulationMutation.data && (
            <Card className="border-primary-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary-400" />
                  Resultado da Simulação
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                    <p className="text-surface-400 text-sm mb-1">Valor Aplicado</p>
                    <p className="text-xl font-bold text-surface-100">
                      {formatCurrency(Number(amount) * 100)}
                    </p>
                  </div>
                  <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                    <p className="text-surface-400 text-sm mb-1">Rendimento Bruto</p>
                    <p className="text-xl font-bold text-surface-100">
                      {formatCurrency(simulationMutation.data.grossReturn)}
                    </p>
                  </div>
                  <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                    <p className="text-surface-400 text-sm mb-1">Impostos</p>
                    <p className="text-xl font-bold text-red-400">
                      -{formatCurrency(simulationMutation.data.taxAmount + simulationMutation.data.iofAmount)}
                    </p>
                  </div>
                  <div className="p-4 bg-primary-500/20 rounded-lg text-center">
                    <p className="text-surface-400 text-sm mb-1">Rendimento Líquido</p>
                    <p className="text-xl font-bold text-primary-400">
                      {formatCurrency(simulationMutation.data.netReturn)}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center p-3 bg-surface-800/30 rounded-lg">
                    <span className="text-surface-400">Valor Final</span>
                    <span className="text-surface-100 font-semibold">
                      {formatCurrency(Number(amount) * 100 + simulationMutation.data.netReturn)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-surface-800/30 rounded-lg">
                    <span className="text-surface-400">Taxa Efetiva</span>
                    <span className="text-primary-400 font-semibold">
                      {(simulationMutation.data.effectiveRate * 100).toFixed(2)}% a.a.
                    </span>
                  </div>
                  {simulationMutation.data.savingsReturn > 0 && (
                    <div className="flex justify-between items-center p-3 bg-green-500/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <PiggyBank className="w-4 h-4 text-green-400" />
                        <span className="text-surface-400">Vantagem sobre Poupança</span>
                      </div>
                      <span className="text-green-400 font-semibold">
                        +{formatCurrency(simulationMutation.data.advantage)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Projection Chart Placeholder */}
                {simulationMutation.data.projectedValues && simulationMutation.data.projectedValues.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-surface-400 mb-3">Projeção de Rendimento</h4>
                    <div className="h-32 bg-surface-800/30 rounded-lg flex items-end justify-around p-4">
                      {simulationMutation.data.projectedValues.slice(0, 6).map((pv, idx) => {
                        const maxValue = Math.max(...simulationMutation.data!.projectedValues.map((v) => v.netValue));
                        const height = (pv.netValue / maxValue) * 100;
                        return (
                          <div key={idx} className="flex flex-col items-center gap-1">
                            <div
                              className="w-8 bg-primary-500 rounded-t"
                              style={{ height: `${height}%` }}
                            />
                            <span className="text-xs text-surface-500">
                              {new Date(pv.date).toLocaleDateString('pt-BR', { month: 'short' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/banking/investments/${selectedProductId}`)}
                  >
                    Ver Produto
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compare Mode */}
          {compareMode && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Comparar Produtos
                  <Badge variant="default">{compareProducts.length}/3</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-surface-400 mb-4">
                  Selecione até 3 produtos para comparar
                </p>
                {isLoadingProducts ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {productsData?.products?.slice(0, 10).map((product) => (
                      <div
                        key={product.id}
                        onClick={() => toggleCompareProduct(product.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          compareProducts.includes(product.id)
                            ? 'bg-primary-500/20 border border-primary-500/50'
                            : 'bg-surface-800/50 hover:bg-surface-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-surface-100 font-medium">{product.name}</p>
                            <p className="text-sm text-surface-400">{product.issuer}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-primary-400 font-semibold">{formatRate(product)}</p>
                            <Badge variant="default">{typeLabels[product.type]}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {compareProducts.length >= 2 && (
                  <Button variant="primary" className="w-full mt-4" onClick={handleCompare}>
                    Comparar Selecionados
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Selected Product Info */}
          {selectedProduct && (
            <Card>
              <CardHeader>
                <CardTitle>Produto Selecionado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-surface-100 font-medium">{selectedProduct.name}</p>
                  <p className="text-sm text-surface-400">{selectedProduct.issuer}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="primary">{typeLabels[selectedProduct.type]}</Badge>
                  {selectedProduct.taxExempt && <Badge variant="success">Isento IR</Badge>}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-surface-400">Rentabilidade</span>
                    <span className="text-primary-400">{formatRate(selectedProduct)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Mínimo</span>
                    <span className="text-surface-100">{formatCurrency(selectedProduct.minimumAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Liquidez</span>
                    <span className="text-surface-100">D+{selectedProduct.liquidityDays}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tax Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Tabela de IR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Até 180 dias</span>
                <span className="text-surface-100">22,5%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">181 a 360 dias</span>
                <span className="text-surface-100">20%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">361 a 720 dias</span>
                <span className="text-surface-100">17,5%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Acima de 720 dias</span>
                <span className="text-green-400">15%</span>
              </div>
              <p className="text-xs text-surface-500 mt-3">
                LCI e LCA são isentos de IR para pessoa física.
              </p>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => navigate('/banking/investments')}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Ver Marketplace
              </Button>
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => navigate('/banking/investments/portfolio')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Meu Portfólio
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
